import { useState, useCallback, useMemo } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qDiagnosesByEncounter,
  qDiagnosisSuggestions,
  qPendingDiagnosisSuggestionsByEncounter,
  type PatientDiagnosisEntryRow,
  type PatientDiagnosisSuggestionRow,
  type VttSuggestionRow,
} from '@helios/data-access';
import { ChipListField, type Chip } from './ChipListField';
import { AiSuggestedDiagnosisCard } from './AiSuggestedDiagnosisCard';
import { DermatologyAnalysisDrawer } from './DermatologyAnalysisDrawer';

/* ------------------------------------------------------------------ */
/*  Diagnosis structured entry (PRD.214 Phase 2)                      */
/*  Plus AI Suggested diagnosis cards from PRD.217.                   */
/* ------------------------------------------------------------------ */

interface Props {
  patientId: string;
  encounterId: string;
  /** When true, hide the internal "Diagnosis" header (used inside guided-note cards). */
  hideHeader?: boolean;
}

export function EncounterDiagnosesTab({ patientId, encounterId, hideHeader }: Props) {
  const { db } = usePowerSyncDb();
  const { user, claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';

  const { data: entries = [] } = usePowerSyncQuery<PatientDiagnosisEntryRow>(
    qDiagnosesByEncounter(encounterId),
  );

  // PRD.217 — pending AI Suggested cards for this encounter
  const { data: aiSuggestions = [] } = usePowerSyncQuery<PatientDiagnosisSuggestionRow>(
    qPendingDiagnosisSuggestionsByEncounter(encounterId),
  );

  const [search, setSearch] = useState('');
  const { data: suggestionRows = [] } = usePowerSyncQuery<VttSuggestionRow>(
    qDiagnosisSuggestions(orgId, search),
  );
  const suggestions = useMemo(() => suggestionRows.map((r) => r.label), [suggestionRows]);

  const chips: Chip[] = useMemo(
    () => entries.map((e) => ({ id: e.id, label: e.label })),
    [entries],
  );

  const handleAdd = useCallback(
    async (label: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_diagnosis_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), orgId, null, patientId, encounterId, label, now, now],
      );
    },
    [db, orgId, patientId, encounterId],
  );

  const handleRemove = useCallback(
    async (chip: Chip) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_diagnosis_entries WHERE id = ?`, [chip.id]);
    },
    [db],
  );

  // ─── AI analysis drawer state ───
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRunId, setDrawerRunId] = useState<string | null>(null);
  const openDetails = useCallback((analysisRunId: string) => {
    setDrawerRunId(analysisRunId);
    setDrawerOpen(true);
  }, []);

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Diagnosis
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Working diagnosis for this encounter.
          </p>
        </div>
      )}

      {aiSuggestions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {aiSuggestions.map((s) => (
            <AiSuggestedDiagnosisCard
              key={s.id}
              suggestion={s}
              userId={userId}
              orgId={orgId}
              patientId={patientId}
              encounterId={encounterId}
              onViewDetails={openDetails}
            />
          ))}
        </div>
      )}

      <ChipListField
        chips={chips}
        suggestions={suggestions}
        onSearchChange={setSearch}
        onAdd={handleAdd}
        onRemove={handleRemove}
        subtitle="Add or edit the diagnosis"
        addLabel="Add Diagnosis"
        placeholder="Add or edit diagnosis…"
      />

      <DermatologyAnalysisDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        encounterId={encounterId}
        analysisRunId={drawerRunId}
      />
    </div>
  );
}
